import { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { validateEmailForSignup } from "@/lib/emailValidation";
import { TurnstileWidget, isTurnstileConfigured } from "@/components/TurnstileWidget";

export function LoginPage() {
  const { signInWithEmail } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");

  const handleVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Client-side guard: short-circuit disposable domains and malformed addresses
    // before we burn a magic-link email send. Server-side trigger is authoritative.
    const check = validateEmailForSignup(email);
    if (!check.ok) {
      setError(check.reason);
      return;
    }

    if (isTurnstileConfigured && !captchaToken) {
      setError("Please complete the verification check below before continuing.");
      return;
    }

    setSending(true);
    // Stash the intended destination so /auth/callback knows where to send the user
    sessionStorage.setItem("doclens.postLoginRedirect", from);
    const result = await signInWithEmail(email.trim(), { captchaToken });
    setSending(false);
    if (result.error) {
      // Supabase Auth surfaces the DB trigger's exception as a generic "Database
      // error saving new user." Map that to something more helpful — the only
      // post-validation reason this fires is the duplicate-alias check.
      const msg = result.error.toLowerCase();
      if (msg.includes("database error") || msg.includes("unexpected_failure")) {
        setError(
          "An account already exists for this email address. Try signing in with your original address.",
        );
      } else {
        setError(result.error);
      }
    } else {
      setSent(true);
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 md:py-24">
      <div className="rounded-xl border border-neutral-200 bg-white p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold">Sign in to DocLens</h1>
          <p className="text-sm text-neutral-600">
            We'll email you a magic link. No password to remember.
          </p>
        </div>

        {sent ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 p-4 text-sm space-y-2">
            <div className="flex items-center gap-2 font-medium">
              <Mail className="h-4 w-4" /> Check your inbox
            </div>
            <p>
              We sent a sign-in link to <strong>{email}</strong>. Click it from any device to
              come back here.
            </p>
            <p className="text-xs text-emerald-800/80">
              Didn't see it? Check spam, or try a different email.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block text-sm font-medium text-neutral-700">
              Email address
              <input
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </label>

            {isTurnstileConfigured && (
              <div className="pt-1">
                <TurnstileWidget onVerify={handleVerify} onExpire={() => setCaptchaToken("")} />
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-900 p-3 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={sending || !email.trim() || (isTurnstileConfigured && !captchaToken)}
              className="w-full rounded-lg bg-brand text-white px-4 py-2.5 font-medium hover:bg-brand-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send magic link"}
            </button>
          </form>
        )}

        <div className="text-xs text-neutral-500 text-center space-y-1">
          <p>New here? Same form — your account is created on first sign-in.</p>
          <p>
            By signing in, you agree to our{" "}
            <Link to="/legal/terms" className="underline">Terms</Link> and{" "}
            <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
